/**
 * アコーディオン式サイドバー。
 *
 * カテゴリ・状態・今後増える絞り込みをここに集約する。
 * 節（Section）を足すだけで増やせる構造にしてあるので、
 * 機能追加のたびにレイアウトを作り直さなくて済む。
 *
 * 開閉状態は localStorage に持つ。毎回同じ節を開き直す手間は
 * 日に何度も使う画面では地味に効く。
 *
 * 開閉はアニメーションする（CSS の grid-template-rows 0fr→1fr）。
 * 中身の高さを測らずに済むので、項目数が変わっても壊れない。
 *
 * パネル自体（SidebarPanel）も畳める・幅を変えられる。畳むと 48px のレールになり、
 * 下部の「設定」だけアイコンで残す（狭い画面では開閉ボタンと横並びの1行になる）。
 * 幅と開閉は localStorage（sidebar.layout）に持つ。
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { usePersistentState } from '../lib/usePersistentState.ts'

const STORAGE_KEY = 'sidebar.open-sections'

function loadOpenState(): Record<string, boolean> {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, boolean>
  } catch {
    return {}
  }
}

export function useSidebarSections(defaults: Record<string, boolean>) {
  const [open, setOpen] = useState<Record<string, boolean>>(() => ({
    ...defaults,
    ...loadOpenState(),
  }))

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(open))
    } catch {
      // プライベートブラウジング等で書けないことがある。開閉が保存されないだけ。
    }
  }, [open])

  const toggle = (id: string) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }))
  return { open, toggle }
}

export function SidebarSection({
  id,
  title,
  badge,
  open,
  onToggle,
  children,
}: {
  id: string
  title: string
  /** 件数など。閉じていても中身の規模が分かるように。 */
  badge?: ReactNode
  open: boolean
  onToggle: (id: string) => void
  children: ReactNode
}) {
  const contentId = `sidebar-${id}`
  return (
    <section className="side-section">
      <h2>
        <button
          type="button"
          className="side-head"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => onToggle(id)}
        >
          <span className={`chev${open ? ' open' : ''}`} aria-hidden="true">
            ▸
          </span>
          <span className="side-title">{title}</span>
          {badge !== undefined && <span className="side-badge">{badge}</span>}
        </button>
      </h2>
      {/* 開閉をアニメーションさせるため、閉じていても DOM には残す。
          代わりに inert で、タブ移動と読み上げの対象から外す
          （外さないと見えない項目にフォーカスが入り込む）。 */}
      <div className={`side-collapse${open ? ' open' : ''}`} inert={!open}>
        <div className="side-collapse-inner">
          <div className="side-body" id={contentId}>
            {children}
          </div>
        </div>
      </div>
    </section>
  )
}

/** サイドバー内の選択肢1件。 */
export function SidebarItem({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean
  onClick: () => void
  count?: number
  children: ReactNode
}) {
  return (
    <button
      type="button"
      className={active ? 'side-item active' : 'side-item'}
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
    >
      <span className="side-item-label">{children}</span>
      {count !== undefined && <span className="side-count">{count}</span>}
    </button>
  )
}

/**
 * チェックボックス式の項目。種別のように「複数を同時に選ぶ」絞り込みに使う。
 * SidebarItem（排他選択）と見た目の行高を揃えてある。
 */
export function SidebarCheckItem({
  checked,
  onChange,
  count,
  children,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  count?: number
  children: ReactNode
}) {
  return (
    <label className={checked ? 'side-check checked' : 'side-check'}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="side-item-label">{children}</span>
      {count !== undefined && <span className="side-count">{count}</span>}
    </label>
  )
}

/* ------------------------------------------------------------------ */
/* パネル全体（開閉・幅）                                               */
/* ------------------------------------------------------------------ */

export const SIDEBAR_DEFAULT_WIDTH = 256
export const SIDEBAR_MIN_WIDTH = 200
export const SIDEBAR_MAX_WIDTH = 440
/** 畳んだときのレール幅。開閉ボタンと設定アイコンが収まる最小限。 */
export const SIDEBAR_RAIL_WIDTH = 48

type SidebarLayout = { collapsed: boolean; width: number }

const clampWidth = (w: number): number =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(w)))

/** 保存値は型の保証が無い（古い版・手書き）ので、使う前に必ず既知の形へ丸める。 */
const sanitizeLayout = (raw: unknown): SidebarLayout => {
  const r = (raw ?? {}) as Partial<SidebarLayout>
  return {
    collapsed: r.collapsed === true,
    width:
      typeof r.width === 'number' && Number.isFinite(r.width)
        ? clampWidth(r.width)
        : SIDEBAR_DEFAULT_WIDTH,
  }
}

export function useSidebarLayout() {
  const [raw, setRaw] = usePersistentState<SidebarLayout>('sidebar.layout', {
    collapsed: false,
    width: SIDEBAR_DEFAULT_WIDTH,
  })
  const layout = sanitizeLayout(raw)
  const toggleCollapsed = useCallback(
    () => setRaw((prev) => ({ ...sanitizeLayout(prev), collapsed: !sanitizeLayout(prev).collapsed })),
    [setRaw],
  )
  const setWidth = useCallback(
    (width: number) =>
      setRaw((prev) => {
        const cur = sanitizeLayout(prev)
        const next = clampWidth(width)
        // 変わらないなら同じ参照を返す（再レンダーも localStorage 書き込みも起きない）
        return next === cur.width ? prev : { ...cur, width: next }
      }),
    [setRaw],
  )
  return { collapsed: layout.collapsed, width: layout.width, toggleCollapsed, setWidth }
}

/** 歯車。設定への入口に使う（サイドバー下部＝慣例の位置）。 */
export function GearIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M6.6 1.8a.7.7 0 0 1 .7-.6h1.4a.7.7 0 0 1 .7.6l.2 1.3c.44.16.85.4 1.22.68l1.24-.5a.7.7 0 0 1 .87.3l.7 1.22a.7.7 0 0 1-.17.9l-1.03.83a4.9 4.9 0 0 1 0 1.4l1.03.82a.7.7 0 0 1 .17.9l-.7 1.23a.7.7 0 0 1-.87.3l-1.24-.5a4.9 4.9 0 0 1-1.21.69l-.21 1.32a.7.7 0 0 1-.7.59H7.3a.7.7 0 0 1-.7-.59l-.2-1.32a4.9 4.9 0 0 1-1.22-.7l-1.24.5a.7.7 0 0 1-.87-.29l-.7-1.22a.7.7 0 0 1 .17-.9l1.03-.83a4.9 4.9 0 0 1 0-1.4l-1.03-.83a.7.7 0 0 1-.17-.9l.7-1.22a.7.7 0 0 1 .87-.3l1.24.5c.37-.29.78-.52 1.21-.69l.21-1.32zM8 10.1a2.1 2.1 0 1 0 0-4.2 2.1 2.1 0 0 0 0 4.2z" />
    </svg>
  )
}

/**
 * サイドバーの外枠。開閉ボタン・中身・下部（設定など）・幅の取っ手を持つ。
 *
 * 畳むときは幅だけをアニメーションさせ、中身は開いたときの幅のまま固定して
 * overflow で隠す（幅に追従させると文字が折り返しながら縮んで見苦しい）。
 * 畳んだ中身は inert で、タブ移動と読み上げの対象から外す。
 *
 * 取っ手はドラッグのほかキーボード（←→ で 16px、Home/End で最小・最大）でも動かせ、
 * ダブルクリックか Enter で既定幅に戻る。ドラッグ中は transition を切る（追従が鈍る）。
 *
 * ドラッグ中の幅はローカル state に持ち、親（永続 state）へは離したときに1回だけ渡す。
 * 毎回親へ流すとカタログ全体が再描画され、localStorage にも1ピクセルごとに書かれる。
 */
export function SidebarPanel({
  collapsed,
  width,
  onToggle,
  onResize,
  footer,
  children,
}: {
  collapsed: boolean
  width: number
  onToggle: () => void
  onResize: (width: number) => void
  /** 下部に固定するもの（設定ボタンなど）。畳んでもアイコンだけ残す。 */
  footer?: ReactNode
  children: ReactNode
}) {
  const [resizing, setResizing] = useState(false)
  // ドラッグ中だけ使う仮の幅。null なら親から渡された幅をそのまま使う
  const [dragWidth, setDragWidth] = useState<number | null>(null)
  const drag = useRef<{ startX: number; startWidth: number; lastWidth: number | null } | null>(null)
  const shownWidth = dragWidth ?? width

  // ドラッグ中は本文を選択させず、カーソルを取っ手の外でも col-resize に保つ。
  // inline の user-select は Safari で効かない（-webkit- 接頭辞が要る）ので、
  // body にクラスを付けて CSS 側で両方を当てる。
  useEffect(() => {
    if (!resizing) return
    document.body.classList.add('sidebar-resizing')
    return () => document.body.classList.remove('sidebar-resizing')
  }, [resizing])

  // ドラッグ中に畳まれると取っ手ごとアンマウントされ pointerup が届かない。手動で後始末する
  useEffect(() => {
    if (!collapsed) return
    drag.current = null
    setDragWidth(null)
    setResizing(false)
  }, [collapsed])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    drag.current = { startX: e.clientX, startWidth: width, lastWidth: null }
    // キャプチャできない環境（合成イベント・古い WebKit）でもドラッグ自体は続ける
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // 取っ手の外へ出たら追従しなくなるだけ
    }
    setResizing(true)
    e.preventDefault()
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    const next = clampWidth(drag.current.startWidth + (e.clientX - drag.current.startX))
    drag.current.lastWidth = next
    setDragWidth(next)
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    // pointercancel は座標が当てにならないので、最後に見せていた幅で確定する
    const final = drag.current.lastWidth
    drag.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // キャプチャしていなければ何もしない
    }
    if (final !== null) onResize(final)
    setDragWidth(null)
    setResizing(false)
  }
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = 16
    if (e.key === 'ArrowLeft') onResize(width - step)
    else if (e.key === 'ArrowRight') onResize(width + step)
    else if (e.key === 'Home') onResize(SIDEBAR_MIN_WIDTH)
    else if (e.key === 'End') onResize(SIDEBAR_MAX_WIDTH)
    else if (e.key === 'Enter') onResize(SIDEBAR_DEFAULT_WIDTH) // ダブルクリックと同じ（キーボード用）
    else return
    e.preventDefault()
  }

  const style = {
    '--sidebar-open-w': `${shownWidth}px`,
    '--sidebar-w': collapsed ? `${SIDEBAR_RAIL_WIDTH}px` : `${shownWidth}px`,
  } as CSSProperties

  return (
    <aside
      className={`sidebar${collapsed ? ' collapsed' : ''}${resizing ? ' resizing' : ''}`}
      style={style}
      aria-label="絞り込み"
    >
      <div className="sidebar-top">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'サイドバーを開く' : 'サイドバーを閉じる'}
          title={collapsed ? 'サイドバーを開く' : 'サイドバーを閉じる'}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M10 3 5 8l5 5" />
            <path d="M3 2v12" />
          </svg>
        </button>
      </div>
      <div className="sidebar-body" inert={collapsed}>
        {children}
      </div>
      {footer && <div className="sidebar-foot">{footer}</div>}
      {!collapsed && (
        <div
          className={`sidebar-resizer${resizing ? ' active' : ''}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="サイドバーの幅"
          aria-valuenow={shownWidth}
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          title="ドラッグで幅を変更（ダブルクリックか Enter で既定幅）"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => onResize(SIDEBAR_DEFAULT_WIDTH)}
          onKeyDown={onKeyDown}
        />
      )}
    </aside>
  )
}

/* ------------------------------------------------------------------ */
/* 木（カテゴリ階層）                                                   */
/* ------------------------------------------------------------------ */

export type SidebarTreeNode = {
  id: string
  label: string
  /** 配下を含む件数。 */
  count?: number
  children: SidebarTreeNode[]
}

/**
 * 入れ子の一覧。カテゴリ階層（最大3段）に使う。
 *
 * 行は「開閉の矢印」と「選択ボタン」の2つに分ける。1つのボタンにすると
 * 「開きたいだけなのに選択が変わる」「選びたいだけなのに畳まれる」が起きる。
 * 子が無い行は矢印の幅だけ空けて、ラベルの位置を揃える。
 * 段差は --depth を CSS に渡して padding で付ける（入れ子の ul に margin を
 * 付けると、選択時の帯が段ごとに短くなって見苦しい）。
 */
export function SidebarTree({
  nodes,
  activeId,
  onSelect,
  expanded,
  onToggleExpand,
  depth = 1,
}: {
  nodes: SidebarTreeNode[]
  activeId: string | null
  onSelect: (id: string) => void
  /** id → 開いているか。無ければ開いている扱い。 */
  expanded: Record<string, boolean>
  onToggleExpand: (id: string) => void
  depth?: number
}) {
  return (
    <ul className="side-tree">
      {nodes.map((n) => {
        const hasKids = n.children.length > 0
        const open = expanded[n.id] ?? true
        const active = activeId === n.id
        return (
          <li key={n.id}>
            <div
              className={active ? 'side-tree-row active' : 'side-tree-row'}
              style={{ '--depth': depth } as CSSProperties}
            >
              {hasKids ? (
                <button
                  type="button"
                  className="side-tree-chev"
                  aria-expanded={open}
                  aria-label={open ? `${n.label} を閉じる` : `${n.label} を開く`}
                  onClick={() => onToggleExpand(n.id)}
                >
                  <span className={`chev${open ? ' open' : ''}`} aria-hidden="true">
                    ▸
                  </span>
                </button>
              ) : (
                <span className="side-tree-chev placeholder" aria-hidden="true" />
              )}
              <button
                type="button"
                className="side-tree-label"
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelect(n.id)}
              >
                <span className="side-item-label">{n.label}</span>
                {n.count !== undefined && <span className="side-count">{n.count}</span>}
              </button>
            </div>
            {hasKids && open && (
              <SidebarTree
                nodes={n.children}
                activeId={activeId}
                onSelect={onSelect}
                expanded={expanded}
                onToggleExpand={onToggleExpand}
                depth={depth + 1}
              />
            )}
          </li>
        )
      })}
    </ul>
  )
}
